/**
 * State for the page-transition loader.
 *
 * Two things the UI needs and Nuxt's own loading state does not provide:
 *  - where the user is heading, so the loader can wear that section's colour
 *  - something worth reading, which here means real platform numbers
 *
 * The stats are fetched once, lazily, well after the first navigation, and then
 * kept in module scope. A loader that fetches while you wait would be absurd.
 */

interface LoaderSection {
    /** Section accent, mirroring the nav bar (the source of truth for section colours) */
    color: string
    label: string
}

interface PlatformStats {
    usersCount: number
    mentorsCount: number
    orgsCount: number
    opportunitiesCount: number
}

const BRAND = '#017275'

const SECTIONS: Array<{ match: RegExp; section: LoaderSection }> = [
    { match: /^\/(mentori|mentor|mentoring|mentees)(\/|$)/, section: { color: '#047274', label: 'Mentoring' } },
    { match: /^\/praca(\/|$)/, section: { color: '#f97316', label: 'Práca a stáže' } },
    { match: /^\/dobrovolnictvo(\/|$)/, section: { color: '#eab308', label: 'Dobrovoľníctvo' } },
    { match: /^\/vzdelavanie(\/|$)/, section: { color: '#c24cfc', label: 'Vzdelávanie' } },
    { match: /^\/sebapoznanie(\/|$)/, section: { color: '#ec4899', label: 'Sebapoznanie' } },
    { match: /^\/(organizacie|pre-organizacie|pre-neziskovky)(\/|$)/, section: { color: BRAND, label: 'Organizácie' } },
    { match: /^\/blog(\/|$)/, section: { color: BRAND, label: 'Blog' } }
]

// Shown before the live numbers arrive (and if the request ever fails). Deliberately
// rounded down from the real figures so they can never overstate the platform.
const FALLBACK_STATS: PlatformStats = {
    usersCount: 20000,
    mentorsCount: 590,
    orgsCount: 1700,
    opportunitiesCount: 3100
}

const stats = ref<PlatformStats>(FALLBACK_STATS)
const statsLoaded = ref(false)
let statsRequested = false

const nf = new Intl.NumberFormat('sk-SK')

/** Lines with personality. Never phrased as statistics — only the numbers above are real. */
const TIPS: string[] = [
    'Najlepší mentor nie je ten najznámejší, ale ten, čo rieši tvoju tému.',
    'Dobrovoľníctvo je najrýchlejší spôsob, ako si vyskúšať odbor bez záväzku.',
    'V profile staví na to, čo ťa naozaj zaujíma — nie na to, čo znie dobre.',
    'Prvá správa mentorovi nemusí byť dokonalá. Musí byť odoslaná.',
    'Rastieme pomaly, zato do hĺbky. Chvíľku strpenia.'
]

function factsFor(s: PlatformStats): string[] {
    return [
        `Na GROWNi je ${nf.format(s.mentorsCount)} mentoriek a mentorov, ktorí ponúkajú svoj čas.`,
        `Práve teraz je otvorených ${nf.format(s.opportunitiesCount)} príležitostí.`,
        `${nf.format(s.orgsCount)} organizácií tu hľadá ľudí.`,
        `${nf.format(s.usersCount)} ľudí si tu buduje svoju cestu.`,
        ...TIPS
    ]
}

export function useGrowniLoader() {
    const facts = computed(() => factsFor(stats.value))

    function sectionFor(path: string): LoaderSection {
        for (const { match, section } of SECTIONS) {
            if (match.test(path)) return section
        }
        return { color: BRAND, label: 'GROWNi' }
    }

    /**
     * Warms the numbers up once, on idle, so no navigation ever waits on this.
     * Failure is silent by design — the fallback copy is already sensible.
     */
    function warmUpStats() {
        if (statsRequested || !import.meta.client) return
        statsRequested = true

        const run = () => {
            $fetch<PlatformStats>('/api/homepage/statistics')
                .then((data) => {
                    if (data && typeof data.mentorsCount === 'number') {
                        stats.value = data
                        statsLoaded.value = true
                    }
                })
                .catch(() => {
                    /* keep the fallback numbers */
                })
        }

        if (typeof requestIdleCallback === 'function') {
            requestIdleCallback(run, { timeout: 4000 })
        } else {
            setTimeout(run, 2000)
        }
    }

    return { facts, sectionFor, warmUpStats, stats, statsLoaded }
}
